import { createContext, useContext, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * Lets a page render buttons into the app header's right-hand slot.
 *
 * The design puts page-level actions (Export, and later others) in the top bar
 * rather than repeating a toolbar per page, but the header lives in Layout and
 * the buttons need page state. A portal keeps the ownership right: the page
 * still renders and controls the button, it just lands in the header's DOM.
 */
const HeaderSlotContext = createContext<HTMLElement | null>(null);

export function HeaderActionsProvider({ children }: { children: (slot: ReactNode) => ReactNode }) {
  const [node, setNode] = useState<HTMLElement | null>(null);
  return (
    <HeaderSlotContext.Provider value={node}>
      {children(<div ref={setNode} className="flex items-center gap-2" />)}
    </HeaderSlotContext.Provider>
  );
}

export function HeaderActions({ children }: { children: ReactNode }) {
  const slot = useContext(HeaderSlotContext);
  // null on the first render, before Layout's ref callback has run.
  if (!slot) return null;
  return createPortal(children, slot);
}
